import { isEmpty } from 'lodash';
import {
    FEEDBACK_SOURCE_LABELS,
    FeedbackSource,
    OTHER_OPTION_KEY,
} from 'app/modules/user/createTicket/customerImpact/types';
import { formatCustomerSummary, NO_CUSTOMER_PLACEHOLDER } from './customerDisplay';
import {
    isFeatureRequestVariant,
    readText,
    toViewRequest,
    ViewCustomerImpact,
    ViewRequest,
} from './requestFields';
import { CustomerSummaryItem } from './types';

/**
 * "General Feedback" detection and the copy that replaces customer information
 * on those tickets.
 *
 * A General Feedback ticket is a **new-flow** ticket submitted with no customer
 * attached. Per the PRD it is only offered on the Feature Request (PFR) type, so
 * all three conditions must hold:
 *
 *  1. the request type resolves to the Feature Request variant,
 *  2. no named customer is attached, and
 *  3. the ticket came from the new create-ticket flow.
 *
 * (3) is answered by `submittedViaNewFlow`, the boolean the backend stamps at
 * submission time. See {@link isNewFlowTicket} for why a legacy fallback remains.
 */

/** Customer text shown on list cards and the detail header for these tickets. */
export const GENERAL_FEEDBACK_CUSTOMER_LABEL = 'General feedback';

/** Empty-state card title (replaces the per-customer cards). */
export const GENERAL_FEEDBACK_EMPTY_TITLE = 'General Feedback - No customer attached';

/** Empty-state card helper text. */
export const GENERAL_FEEDBACK_EMPTY_HELPER =
    'This ticket was submitted as general feedback and is not tied to a specific customer.';

/**
 * `whyThisDateIsImportant` placeholder posted by the new create flow, which has
 * no time-sensitivity step. Compared case-insensitively.
 */
export const NEW_FLOW_DATE_IMPORTANCE_MARKER = 'not specified';

/** True when at least one attached customer has a usable name. */
export const hasAttachedCustomer = (customers?: ViewCustomerImpact[]): boolean =>
    (customers ?? []).some((customer) => !isEmpty(readText(customer?.customerName)));

/** True when the ticket was created through the new ticket flow. */
export const isNewFlowTicket = (request?: unknown): boolean => {
    const view = toViewRequest(request);

    // The backend stamps this at submission time, so it is the authoritative answer.
    if (view.submittedViaNewFlow === true) {
        return true;
    }

    // Fallback for tickets created by the new flow *before* `submittedViaNewFlow` existed:
    // those documents have no such field (Cosmos is schema-on-read, and the PRD explicitly
    // rules out a data migration), so they are still recognised by their payload shape —
    // the new flow has no date step, so it posted no requested resolution date and the
    // literal 'Not specified' placeholder. Remove once those tickets have aged out.
    return (
        !view.requestedResolutionDate &&
        readText(view.whyThisDateIsImportant).toLowerCase() === NEW_FLOW_DATE_IMPORTANCE_MARKER
    );
};

/**
 * True when the ticket was submitted through the new flow with no customer
 * attached — i.e. the seller used the General Feedback tab.
 *
 * Deliberately not gated on the Feature Request variant. The create flow offers
 * that tab for every request type, so gating here hid answers the seller had
 * actually given. Restricting which types may be submitted as general feedback
 * belongs in the create flow and is tracked separately.
 */
const isCustomerlessNewFlowTicket = (view: ViewRequest): boolean =>
    !hasAttachedCustomer(view.customerImpacts) && isNewFlowTicket(view);

/**
 * True when the ticket is a new-flow Feature Request submitted as general
 * feedback.
 *
 * The Feature Request condition mirrors PRD Assumption 8 ("General Feedback is
 * only allowed on Feature Request tickets") and drives the customerless empty
 * state and the `General feedback` customer label.
 */
export const isGeneralFeedbackTicket = (request?: unknown): boolean => {
    const view = toViewRequest(request);
    return isFeatureRequestVariant(view.requestType) && isCustomerlessNewFlowTicket(view);
};

/** Options for {@link resolveCustomerSummaryText}. */
export interface CustomerSummaryTextOptions {
    /** Applied to the displayed first name only (mirrors `formatCustomerSummary`). */
    formatName?: (name: string) => string;
    /** Rendered when the ticket simply has no customer and is not general feedback. */
    fallback?: string;
}

/**
 * Customer text for list cards and detail headers: `General feedback` for
 * general-feedback tickets, otherwise the existing `[first name] +N more`
 * summary (or the surface's own no-customer fallback).
 */
export const resolveCustomerSummaryText = (
    request?: unknown,
    options: CustomerSummaryTextOptions = {},
): string => {
    const view = toViewRequest(request);
    if (isGeneralFeedbackTicket(view)) {
        return GENERAL_FEEDBACK_CUSTOMER_LABEL;
    }

    const customers: CustomerSummaryItem[] = (view.customerImpacts ?? []).map((customer) => ({
        accountName: customer?.customerName,
    }));
    const summary = formatCustomerSummary(customers, options.formatName);
    return summary === NO_CUSTOMER_PLACEHOLDER
        ? options.fallback ?? NO_CUSTOMER_PLACEHOLDER
        : summary;
};

/** Resolved "Feedback source" card content. */
export interface FeedbackSourceDisplay {
    /** Display label for the selected source. */
    label: string;
    /** Free text captured when the source is "Other"; empty otherwise. */
    details: string;
}

/**
 * Resolves the Feedback source card content, or `undefined` when the ticket is
 * not general feedback or the source has not been captured/persisted yet.
 */
export const resolveFeedbackSource = (request?: unknown): FeedbackSourceDisplay | undefined => {
    const view = toViewRequest(request);
    // Not gated on the Feature Request variant, so feedback captured on any request
    // type is still shown rather than silently dropped.
    if (!isCustomerlessNewFlowTicket(view)) {
        return undefined;
    }

    const raw = readText(view.feedbackSource);
    if (isEmpty(raw)) {
        return undefined;
    }

    const label = FEEDBACK_SOURCE_LABELS[raw as FeedbackSource] ?? raw;
    // The create flow persists the display label ("Other"), older payloads the option key
    // ("other"), so match either before deciding whether the free text applies.
    const isOther = raw.toLowerCase() === OTHER_OPTION_KEY;
    const details = isOther ? readText(view.feedbackSourceDetails) : '';
    return { label, details };
};
